import { h, replaceContent } from '../lib/dom.js';
import { money, pct } from '../lib/format.js';
import { listListings, deleteListing, getIngestResults, saveIngestResults, getCriteria, saveCriteria, resetCriteria, saveListing, getListing } from '../lib/storage.js';
import { assessSubdivision, verdictPresentation } from '../../shared/subdivision.js';
import { normalizeCriteria } from '../../shared/criteria.js';
import { suburbFromPostcode } from '../../shared/sa-postcodes.js';
import { lookupSappa } from '../lib/sappa.js';
import { estimateFrontage } from '../../shared/geometry.js';
import { calculateFeaso } from '../../shared/feaso.js';
import { scoreListing } from '../../shared/scoring.js';
import { TIER2_DEFAULTS } from '../../shared/defaults.js';
import { navigate } from '../lib/router.js';

export function FinderPage() {
  const root = h('div', { class: 'max-w-[1200px] mx-auto px-6 md:px-10' });
  let activeTab = 'shortlist';

  render();
  return root;

  function render() {
    root.innerHTML = '';

    // Header
    root.appendChild(h('header', { class: 'pt-10 pb-6 flex items-end justify-between gap-6 flex-wrap border-b border-[color:var(--color-rule)]' },
      h('div', null,
        h('div', { class: 'folio' }, 'Finder · Adelaide SA'),
        h('h1', { class: 'display display-md mt-1' }, 'The next site, already scored.'),
      ),
      h('div', { class: 'flex gap-2' },
        h('a', { href: '#/finder/new', class: 'btn btn-ghost' }, 'Add manually'),
      ),
    ));

    // Tabs
    root.appendChild(h('div', { class: 'flex gap-4 pt-4 pb-2 border-b border-[color:var(--color-rule)]' },
      tabBtn('shortlist', 'Shortlist'),
      tabBtn('manual', 'Manual listings'),
    ));

    if (activeTab === 'shortlist') {
      root.appendChild(shortlistTab());
    } else {
      root.appendChild(manualTab());
    }
  }

  function tabBtn(key, label) {
    const active = activeTab === key;
    return h('button', {
      type: 'button',
      class: active
        ? 'text-[color:var(--color-ink)] border-b-2 border-[color:var(--color-ink)] pb-2 folio'
        : 'text-[color:var(--color-ink-soft)] pb-2 folio hover:text-[color:var(--color-ink)]',
      onclick: () => { activeTab = key; render(); },
    }, label);
  }

  // ─── Shortlist tab ───────────────────────────────────────────────────

  function currentCriteria() {
    return normalizeCriteria(getCriteria() ?? {});
  }

  function shortlistTab() {
    const cached = getIngestResults();
    const container = h('div', { class: 'py-6 space-y-6' });
    const resultsEl = h('div');

    // Always-visible criteria panel above results.
    const criteriaEl = h('div');
    const renderCriteria = () => replaceContent(criteriaEl, criteriaPanel(resultsEl, criteriaEl));
    renderCriteria();
    container.appendChild(criteriaEl);
    container.appendChild(resultsEl);

    if (!cached) {
      replaceContent(resultsEl, emptyShortlist());
    } else {
      replaceContent(resultsEl, shortlistContent(cached, resultsEl));
    }
    return container;
  }

  function criteriaPanel(resultsEl, criteriaEl, opts = {}) {
    let c = currentCriteria();
    const wrap = h('details', {
      class: 'border border-[color:var(--color-rule)] rounded-[2px]',
      open: !!opts.open,
    });

    // Live-updating summary chip (below "Criteria" label).
    const summaryChip = h('span', { class: 'text-[color:var(--color-ink-faint)] ml-3 normal-case tracking-normal text-xs' });
    const renderChip = () => {
      summaryChip.textContent = `${c.suburbs.length} suburbs · ${c.land_min_m2}\u2013${c.land_max_m2} m² · ≤ ${money(c.price_max)}`;
    };
    renderChip();

    // "Saved" indicator that flashes briefly after each edit.
    const savedIndicator = h('span', {
      class: 'folio text-[color:var(--color-positive)] opacity-0 transition-opacity duration-200 ml-3',
    }, '✓ Saved');
    let savedTimer = null;
    const flashSaved = () => {
      savedIndicator.style.opacity = '1';
      clearTimeout(savedTimer);
      savedTimer = setTimeout(() => { savedIndicator.style.opacity = '0'; }, 1200);
    };

    const summary = h('summary', { class: 'cursor-pointer select-none flex items-baseline justify-between px-5 py-4' },
      h('div', { class: 'folio flex items-baseline' }, 'Criteria', summaryChip, savedIndicator),
      h('span', { class: 'folio text-[color:var(--color-ink-faint)]' }, 'Edit ▾'),
    );
    wrap.appendChild(summary);

    const body = h('div', { class: 'px-5 pb-5 pt-2 border-t border-[color:var(--color-rule)] space-y-5' });

    const update = (patch) => {
      c = { ...c, ...patch };
      saveCriteria(c);
      renderChip();
      flashSaved();
    };

    // Numeric fields row
    body.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-4' },
      numField('Land min (m²)', c.land_min_m2, (v) => update({ land_min_m2: v }), { min: 100, step: 50 }),
      numField('Land max (m²)', c.land_max_m2, (v) => update({ land_max_m2: v }), { min: 100, step: 50 }),
      numField('Price max', c.price_max, (v) => update({ price_max: v }), { min: 100000, step: 10000 }, true),
      numField('Target dwellings', c.target_dwellings, (v) => update({ target_dwellings: v }), { min: 1, max: 8 }),
    ));

    // Suburbs list
    const suburbsWrap = h('div', { class: 'space-y-2' });
    const renderSuburbs = () => {
      suburbsWrap.innerHTML = '';
      suburbsWrap.appendChild(h('div', { class: 'field-label mb-1' }, 'Suburbs'));
      for (let i = 0; i < c.suburbs.length; i++) {
        const s = c.suburbs[i];
        const nameInput = h('input', {
          type: 'text',
          class: 'field flex-1',
          value: s.name,
          placeholder: 'Suburb name',
          oninput: (e) => { c.suburbs[i].name = e.target.value; update({ suburbs: c.suburbs }); },
        });
        suburbsWrap.appendChild(h('div', { class: 'flex gap-2 items-center' },
          nameInput,
          h('input', {
            type: 'text',
            class: 'field w-24',
            value: s.postcode ?? '',
            placeholder: 'postcode',
            oninput: (e) => { c.suburbs[i].postcode = e.target.value; update({ suburbs: c.suburbs }); },
            onchange: (e) => {
              // Postcode typed on a blank row → resolve the suburb name in
              // place. No re-render: that would destroy the input the user
              // is about to type into and swallow their keystrokes.
              if (!c.suburbs[i].name?.trim()) {
                const resolved = suburbFromPostcode(e.target.value);
                if (resolved) {
                  c.suburbs[i].name = resolved;
                  nameInput.value = resolved;
                  update({ suburbs: c.suburbs });
                }
              }
            },
          }),
          h('button', {
            type: 'button',
            class: 'text-[color:var(--color-ink-faint)] hover:text-[color:var(--color-negative)] text-xs px-2',
            onclick: () => { c.suburbs.splice(i, 1); update({ suburbs: c.suburbs }); renderSuburbs(); },
          }, 'Remove'),
        ));
      }
      suburbsWrap.appendChild(h('div', { class: 'flex gap-2' },
        h('button', {
          type: 'button',
          class: 'btn btn-ghost text-xs',
          onclick: () => { c.suburbs.push({ name: '', postcode: '' }); update({ suburbs: c.suburbs }); renderSuburbs(); },
        }, '+ Add suburb'),
        h('button', {
          type: 'button',
          class: 'btn btn-ghost text-xs ml-auto',
          onclick: () => {
            if (confirm('Reset all criteria to defaults?')) {
              resetCriteria();
              replaceContent(criteriaEl, criteriaPanel(resultsEl, criteriaEl, { open: true }));
            }
          },
        }, 'Reset to defaults'),
      ));
    };
    renderSuburbs();
    body.appendChild(suburbsWrap);

    wrap.appendChild(body);
    return wrap;
  }

  function numField(label, value, onInput, opts = {}, isMoney = false) {
    const display = isMoney && value != null
      ? new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(value)
      : (value ?? '');
    const inputProps = isMoney ? {
      type: 'text', inputmode: 'numeric',
      value: display,
      oninput: (e) => {
        const raw = e.target.value.replace(/[^\d.-]/g, '');
        const n = raw === '' ? undefined : Number(raw);
        onInput(Number.isFinite(n) ? n : undefined);
      },
      onblur: (e) => {
        const raw = e.target.value.replace(/[^\d.-]/g, '');
        if (raw !== '') e.target.value = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(Number(raw));
      },
    } : {
      type: 'number', value: value ?? '',
      min: opts.min, max: opts.max, step: opts.step ?? 1,
      oninput: (e) => {
        const v = e.target.value === '' ? undefined : Number(e.target.value);
        onInput(Number.isFinite(v) ? v : undefined);
      },
    };
    return h('label', { class: 'block' },
      h('span', { class: 'field-label' }, label),
      h('input', { class: 'field', ...inputProps }),
    );
  }

  function emptyShortlist() {
    const container = h('div', { class: 'py-8 space-y-4' });
    container.appendChild(h('div', null,
      h('div', { class: 'folio' }, 'No shortlist yet'),
      h('h2', { class: 'display display-md max-w-[24ch] mt-1' },
        'Tune your criteria above, then run the scan.',
      ),
      h('p', { class: 'text-[color:var(--color-ink-soft)] max-w-[54ch] mt-3' },
        'Takes ~15 seconds. Fetches Domain for each suburb, filters by land size + price + property type, ranks by subdivision verdict (assumes General Neighbourhood). Click any result to pull real SAPPA data.',
      ),
    ));
    container.appendChild(runButton(container));
    return container;
  }

  function shortlistContent(data, container) {
    const top = data.top ?? [];
    const age = data.fetched_at ? timeSince(data.fetched_at) : '';

    return h('div', { class: 'space-y-4' },
      // Summary strip
      h('div', { class: 'flex flex-wrap items-baseline gap-6 py-4 border-b border-[color:var(--color-rule)]' },
        stat('Suburbs', String(data.suburbs ?? '—')),
        stat('Listings', String(data.found ?? '—')),
        data.enriched > 0
          ? stat('New this run', String(data.enriched))
          : stat('Showing', `${top.length} from last scan`),
        age ? h('div', { class: 'folio text-[color:var(--color-ink-faint)] ml-auto' }, `Updated ${age}`) : null,
        runButton(container),
      ),

      // Scan trouble — say so plainly instead of silently showing old rows.
      data.errors > 0 && !data.found ? h('p', { class: 'text-sm text-[color:var(--color-warning)] py-3 border-b border-[color:var(--color-rule)]' },
        `The scan couldn't reach Domain (${data.error_samples?.[0]?.error ?? 'request blocked'}). `
        + (top.length ? `Showing the ${top.length} listings from previous runs.` : ''),
      ) : null,

      // Listings
      top.length ? h('ol', { class: 'divide-y divide-[color:var(--color-rule)]' },
        ...top.map((l, i) => h('li', null, shortlistRow(l, i + 1))),
      ) : h('p', { class: 'text-[color:var(--color-ink-dim)] py-8' }, 'No listings matched your criteria.'),
    );
  }

  function runButton(container) {
    let running = false;
    const btn = h('button', {
      type: 'button',
      class: 'btn btn-ink',
      onclick: async () => {
        if (running) return;
        running = true;
        btn.style.opacity = '0.6';
        try {
          let pass = 1;
          let latestData = null;
          // Loop: each call enriches up to 12 listings (Workers subrequest cap).
          // On subsequent passes, already-enriched listings are in D1 and skipped.
          while (true) {
            btn.textContent = pass === 1 ? 'Scanning suburbs\u2026' : `Enriching batch ${pass}\u2026`;
            const res = await fetch('/api/ingest/run', {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ criteria: currentCriteria() }),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            const data = await res.json();
            if (data.top?.length > 0 || !getIngestResults()?.top?.length) {
              saveIngestResults(data);
            }
            latestData = data.top?.length > 0 ? data : getIngestResults();
            replaceContent(container, shortlistContent(latestData, container));
            // If no remaining un-enriched listings, we're done.
            if (!data.remaining || data.remaining <= 0) break;
            pass++;
          }
        } catch (err) {
          replaceContent(container,
            h('div', { class: 'text-[color:var(--color-negative)] py-4' },
              `Discovery failed: ${err.message}`,
            ),
          );
        }
        running = false;
        btn.textContent = 'Run discovery';
        btn.style.opacity = '1';
      },
    }, 'Run discovery');
    return btn;
  }

  function shortlistRow(l, n) {
    const vp = l.verdict ? verdictPresentation(l.verdict) : null;
    const toneCls = {
      positive: 'text-[color:var(--color-positive)]',
      warning: 'text-[color:var(--color-warning)]',
      negative: 'text-[color:var(--color-negative)]',
      neutral: 'text-[color:var(--color-ink-soft)]',
    }[vp?.tone ?? 'neutral'];

    const md = l.max_dwellings ?? {};
    const maxD = Math.max(md.detached ?? 0, md.attached ?? 0);

    return h('div', { class: 'grid grid-cols-12 gap-4 items-baseline py-5' },
      h('div', { class: 'col-span-1 folio num text-[color:var(--color-ink-faint)]' },
        String(n).padStart(2, '0'),
      ),
      h('div', { class: 'col-span-5 min-w-0' },
        h('a', {
          href: l.url, target: '_blank', rel: 'noopener',
          class: 'display display-sm truncate block',
        }, l.address || 'Untitled'),
        h('div', { class: 'text-sm text-[color:var(--color-ink-dim)] flex flex-wrap gap-2 mt-1 items-baseline' },
          l.land_m2 ? h('span', { class: 'num' }, `${l.land_m2} m²`) : null,
          l.frontage_m ? h('span', { class: 'num text-[color:var(--color-ink-faint)]' }, `${l.frontage_m.toFixed(1)}m front`) : null,
          l.zone ? h('span', { class: 'chip' }, l.zone) : null,
          l.is_corner ? h('span', { class: 'chip' }, 'Corner') : null,
          l.blocked_by_overlay ? h('span', { class: 'chip text-[color:var(--color-negative)]' }, '⚠ Overlay') : null,
          vp ? h('span', { class: `folio ${toneCls}` },
            maxD > 0 ? `1 → ${maxD} · ${vp.label}` : vp.label,
          ) : null,
          !l.sappa_enriched ? h('span', { class: 'folio text-[color:var(--color-ink-faint)]' }, 'provisional') : null,
        ),
      ),
      h('div', { class: 'col-span-3 num text-sm text-[color:var(--color-ink-soft)]' },
        l.price_is_median
          ? h('span', null,
              '~', money(l.price_amount),
              h('span', { class: 'text-[color:var(--color-ink-faint)] text-[0.7rem] ml-1' }, 'median'),
            )
          : (l.price || '—'),
      ),
      h('div', { class: 'col-span-2 num text-sm' },
        md.detached != null ? h('span', null,
          h('span', { class: 'folio mr-1' }, 'det'), String(md.detached),
          h('span', { class: 'text-[color:var(--color-ink-faint)] mx-1' }, '/'),
          h('span', { class: 'folio mr-1' }, 'att'), String(md.attached ?? '—'),
        ) : '—',
      ),
      h('div', { class: 'col-span-1 text-right flex flex-col items-end gap-1 whitespace-nowrap' },
        viewButton(l),
        h('a', {
          href: buildFeasoHref(l),
          class: 'folio whitespace-nowrap cursor-pointer text-[0.7rem] text-[color:var(--color-ink-faint)] hover:text-[color:var(--color-ink)] hover:underline underline-offset-4 decoration-[color:var(--color-olive-500)]',
        }, 'Feaso →'),
      ),
    );
  }

  function viewButton(shortlistEntry) {
    const btn = h('button', {
      type: 'button',
      class: 'folio cursor-pointer whitespace-nowrap text-[color:var(--color-olive-700)] hover:text-[color:var(--color-ink)] hover:underline underline-offset-4 decoration-[color:var(--color-olive-500)] disabled:cursor-wait disabled:opacity-60',
      onclick: async () => {
        const original = btn.textContent;
        btn.textContent = 'Loading\u2026';
        btn.disabled = true;
        try {
          const id = await graduateToDetail(shortlistEntry);
          navigate(`/finder/${id}`);
        } catch (err) {
          btn.textContent = 'Failed';
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2000);
        }
      },
    }, 'Inspect →');
    return btn;
  }

  // ─── Manual tab ──────────────────────────────────────────────────────

  function manualTab() {
    const listings = listListings()
      .sort((a, b) => (b.score?.score ?? 0) - (a.score?.score ?? 0));

    if (listings.length === 0) {
      return h('div', { class: 'py-10 text-center text-[color:var(--color-ink-dim)]' },
        h('p', { class: 'mb-3' }, 'No manually-added listings.'),
        h('a', { href: '#/finder/new', class: 'btn btn-ink' }, 'Add one'),
      );
    }

    return h('ol', { class: 'divide-y divide-[color:var(--color-rule)] py-4' },
      ...listings.map((l, i) => h('li', null, manualRow(l, i + 1))),
    );
  }

  function manualRow(l, n) {
    const score = l.score?.score ?? 0;
    const poc = l.feaso_preview?.profit_on_cost_pct ?? 0;
    const signal =
      poc >= 0.15 ? 'text-[color:var(--color-positive)]'
      : poc >= 0.10 ? 'text-[color:var(--color-warning)]'
      : 'text-[color:var(--color-negative)]';

    const sappa = l.sappa;
    const v = sappa ? assessSubdivision({
      area_m2: sappa.parcel?.area_m2 ?? l.land_m2 ?? null,
      frontage_m: sappa.parcel?.frontage_m ?? l.frontage_m ?? null,
      zone_code: sappa.zone?.code ?? null,
      zone_name: sappa.zone?.name ?? l.parcel?.zone ?? null,
      zone_category: sappa.zone?.category ?? null,
      overlay_summary: sappa.overlay_summary ?? {},
      overlays: sappa.overlays ?? [],
      is_corner: sappa.parcel?.is_corner ?? false,
    }) : null;
    const maxD = v ? Math.max(v.max_dwellings?.detached ?? 0, v.max_dwellings?.attached ?? 0) : null;
    const vLabel = v ? verdictPresentation(v.verdict).label : null;
    const vTone = v ? verdictPresentation(v.verdict).tone : 'neutral';
    const vCls = {
      positive: 'text-[color:var(--color-positive)]',
      warning: 'text-[color:var(--color-warning)]',
      negative: 'text-[color:var(--color-negative)]',
      neutral: 'text-[color:var(--color-ink-soft)]',
    }[vTone];

    return h('div', { class: 'group grid grid-cols-12 gap-4 items-baseline py-6' },
      h('div', { class: 'col-span-1' },
        h('div', { class: 'display display-md num' }, String(score).padStart(2, '0')),
        h('div', { class: 'folio text-[color:var(--color-ink-faint)]' }, '/ 100'),
      ),
      h('a', { href: `#/finder/${l.id}`, class: 'col-span-5 min-w-0' },
        h('div', { class: 'display display-sm truncate' }, l.address || l.name || 'Untitled listing'),
        h('div', { class: 'text-sm text-[color:var(--color-ink-dim)] flex flex-wrap gap-2 mt-1 items-baseline' },
          sappa?.zone?.name ? h('span', { class: 'chip' }, sappa.zone.name) : l.parcel?.zone ? h('span', { class: 'chip' }, l.parcel.zone) : null,
          sappa?.parcel?.is_corner ? h('span', { class: 'chip' }, 'Corner') : null,
          l.land_m2 ? h('span', { class: 'text-[color:var(--color-ink-dim)] num' }, `${l.land_m2} m²`) : null,
          v ? h('span', { class: `folio ${vCls}` },
            maxD != null && maxD > 0 ? `1 → ${maxD} · ${vLabel}` : vLabel,
          ) : null,
        ),
      ),
      h('div', { class: 'col-span-2 num text-[color:var(--color-ink-soft)] text-sm' },
        h('div', null, money(l.price ?? 0)),
      ),
      h('div', { class: `col-span-2 num display display-sm text-right ${signal}` }, money(l.feaso_preview?.forecast_profit ?? 0)),
      h('div', { class: 'col-span-1 text-right num' },
        h('span', { class: signal }, pct(poc)),
      ),
      h('div', { class: 'col-span-1 text-right' },
        h('button', {
          type: 'button',
          class: 'text-[color:var(--color-ink-faint)] hover:text-[color:var(--color-negative)] text-xs opacity-0 group-hover:opacity-100 transition-opacity',
          onclick: () => {
            if (confirm(`Remove "${l.address}"?`)) { deleteListing(l.id); render(); }
          },
        }, 'Remove'),
      ),
    );
  }
}

// Promote a shortlist entry to a persisted listing (so it gets the full
// manual-listing detail view with map + planning + verdict).
// Runs SAPPA enrichment on the fly; saves to localStorage listings.
async function graduateToDetail(entry) {
  const stableId = `ls_domain_${entry.listing_id}`;
  const existing = getListing(stableId);
  // If already graduated and has SAPPA data, jump straight to it.
  if (existing?.sappa) return stableId;

  // SAPPA lookup (5–10s upstream)
  let sappa = null;
  try {
    if (entry.address) sappa = await lookupSappa(entry.address);
  } catch {}

  // Build a "listing" payload matching the shape the detail page expects.
  const targetDwellings = 3;
  const land_m2 = sappa?.parcel?.area_m2 ? Math.round(sappa.parcel.area_m2) : entry.land_m2 ?? null;
  const frontage_m = sappa?.parcel?.polygon_wgs84
    ? estimateFrontage(sappa.parcel.polygon_wgs84)?.estimate ?? null
    : null;

  let feaso_preview = null;
  let score = null;
  if (entry.price_amount) {
    const salePerDwelling = Math.round((entry.price_amount * 1.3) / targetDwellings);
    const feaso = calculateFeaso({
      purchase_price: entry.price_amount,
      dwellings: targetDwellings,
      sale_price_per_dwelling: salePerDwelling,
      dwelling_m2: TIER2_DEFAULTS.dwelling_m2,
      cost_per_m2: TIER2_DEFAULTS.cost_per_m2,
    });
    feaso_preview = {
      forecast_profit: feaso.forecast_profit,
      profit_on_cost_pct: feaso.profit_on_cost_pct,
      net_sales: feaso.sales.net_sales,
    };
    const parcelShape = sappa?.parcel || land_m2 ? {
      zone: sappa?.zone?.name ?? null,
      is_corner: sappa?.parcel?.is_corner ?? null,
      area_m2: land_m2,
      frontage_m,
      overlays: sappa?.overlay_summary ?? {},
    } : null;
    score = scoreListing({
      listing: { price: entry.price_amount, land_m2, frontage_m },
      parcel: parcelShape,
      feaso: { profit_on_cost_pct: feaso.profit_on_cost_pct },
      targetDwellings,
    });
  }

  saveListing({
    id: stableId,
    source: 'domain',
    address: entry.address,
    url: entry.url || null,
    price: entry.price_amount ?? null,
    land_m2,
    frontage_m,
    parcel: sappa?.parcel ? {
      zone: sappa.zone?.name ?? null,
      is_corner: sappa.parcel.is_corner,
      area_m2: land_m2,
      frontage_m,
      overlays: sappa.overlay_summary ?? {},
    } : null,
    sappa,
    target_dwellings: targetDwellings,
    sale_price_per_dwelling: entry.price_amount ? Math.round((entry.price_amount * 1.3) / targetDwellings) : null,
    notes: `Discovered from ingest run ${entry.run_id ?? ''}. Auto-saved for detail view.`,
    feaso_preview,
    score,
  });
  return stableId;
}

// Build a Feaso URL only including params that have useful values.
// Prevents purchase_price=0 when the source price is "Contact Agent".
function buildFeasoHref(l) {
  const params = new URLSearchParams();
  if (l.address) params.set('address', l.address);
  if (l.price_amount && l.price_amount > 0) params.set('purchase_price', l.price_amount);
  const md = l.max_dwellings ?? {};
  if (md.detached) params.set('dwellings', md.detached);
  return `#/feaso?${params.toString()}`;
}

function stat(label, value) {
  return h('div', null,
    h('div', { class: 'folio' }, label),
    h('div', { class: 'display display-sm num mt-1' }, value),
  );
}

function timeSince(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
